import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import translationsJson from '../utils/translations.json'; // Importando o arquivo de traduções locais
import { useRecoilValue } from 'recoil';
import { authAtom } from '../states/usuarios/AuthState';

type LanguagesType = string;
type TranslateFunction = (key: string, variables?: object) => string;

interface TranslationContextData {
    language: LanguagesType;
    translate: TranslateFunction;
    changeLanguage: (languageCode: LanguagesType) => void;
}

interface TranslationItem {
    chave: string;
    valor: string;
}

const TranslationContext = createContext({} as TranslationContextData);
interface TranslationProviderProps {
    children: React.ReactNode;
}

const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
    let lang = window.navigator.language.slice(0, 2);
    switch (lang) {
        case 'en':
            lang = 'en-us';
            break;
        case 'es':
            lang = 'es-ar';
            break;
        default:
            lang = 'pt-br';
            break;
    }

    const user = useRecoilValue(authAtom);
    const idiomaUnidadeOperacional = user?.idioma;
    switch (idiomaUnidadeOperacional) {
        case 'es_AR':
            lang = 'es-ar';
            break;
        default:
            lang = 'pt-br';
            break;
    }

    const [translations, setTranslations] = useState<{ [key: string]: string }>({});
    const [language, setLanguage] = useState<LanguagesType>(lang);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    // Função para carregar as traduções a partir da API

    const fetchTranslations = async (languageCode: LanguagesType) => {
        setLoading(true);
        setError(null); // Limpa erro antes de carregar novas traduções
        try {
            // Mesclar traduções locais (do translations.json) com as traduções da API
            let mergedTranslations = { ...translationsJson, ...translationsJson };
            setTranslations(mergedTranslations);

            const response = await fetch(
                `${process.env["REACT_APP_API_URL"]}/v1/I18n?Aplicacao=consultor-front&Idioma=${languageCode}`// idioma=${languageCode}&aplicacao=contabil-front`
            );

            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.statusText}`);
            }

            const data: TranslationItem[] = await response.json();
            const langObject: { [key: string]: string } = {};

            // Preenche o objeto langObject com as traduções da API
            data.forEach((item) => {
                if (item.chave && item.valor) {
                    langObject[item.chave] = item.valor;
                }
            });

            // Mesclar traduções locais (do translations.json) com as traduções da API
            mergedTranslations = { ...translationsJson, ...langObject };

            setTranslations(mergedTranslations);
        } catch (error) {
            console.error('Erro ao carregar traduções:', error);
            setError('Não foi possível carregar as traduções.');
        } finally {
            setLoading(false);
        }

    };

    // Função para mudar o idioma e carregar as traduções
    const changeLanguage = useCallback((languageCode: LanguagesType) => {
        setLanguage(languageCode);
        fetchTranslations(languageCode);
    }, []);

    // Carregar as traduções do idioma inicial
    useEffect(() => {
        fetchTranslations(language);
    }, [language]);

    // Função para realizar a tradução das chaves
    const translate = useCallback<TranslateFunction>((key: string) => {

        // Retorna a tradução da chave ou a chave se não houver tradução
        return translations[key] || key; // Chave como fallback
    }, [translations, loading]);

    return (
        <TranslationContext.Provider value={{ language, translate, changeLanguage }}>
            {children}
        </TranslationContext.Provider>
    );
};

// Hook para consumir o contexto de tradução
const useTranslation = (): TranslationContextData => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within TranslationProvider');
    }
    return context;
};

export { TranslationProvider, useTranslation };